import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import './KanbanColumn.css';

export default function KanbanColumn({
    status,
    label,
    runs,
    children,
    isOver,
    isDisabled,
    disabledReason,
}) {
    const { setNodeRef } = useDroppable({
        id: status,
        disabled: isDisabled,
        data: {
            type: 'column',
            status: status
        }
    });

    const getColumnIcon = () => {
        const icons = {
            planned: '📋',
            roast: '🔥',
            flavor: '🌿',
            grind: '⚙️',
            brew: '☕',
            mix: '🥤',
            keg: '🛢️',
            can: '🥫',
            completed: '✅'
        };
        return icons[status] || '📦';
    };

    const getColumnColor = () => {
        const colors = {
            planned: '#64748b',
            roast: '#ef4444',
            flavor: '#8b5cf6',
            grind: '#f59e0b',
            brew: '#6b4423',
            mix: '#06b6d4',
            keg: '#3b82f6',
            can: '#10b981',
            completed: '#059669'
        };
        return colors[status] || '#64748b';
    };

    const totalPlannedQty = runs.reduce((sum, run) => sum + (run.planned_quantity || 0), 0);

    return (
        <div
            ref={setNodeRef}
            className={`kanban-column ${isOver ? 'is-over' : ''} ${isDisabled ? 'is-disabled' : ''}`}
            style={{ '--column-color': getColumnColor() }}
            data-column-status={status}
        >
            <div className="kanban-column-header">
                <div className="column-title">
                    <span className="column-icon">{getColumnIcon()}</span>
                    <h3>{label}</h3>
                </div>
                <div className="column-count" style={{ backgroundColor: getColumnColor() }}>
                    {runs.length}
                </div>
            </div>

            {isDisabled && (
                <div className="column-disabled-banner">
                    <span className="disabled-icon">🚫</span>
                    <span className="disabled-text">{disabledReason || 'Stage not required'}</span>
                </div>
            )}

            {runs.length > 0 && (
                <div className="column-summary">
                    <div className="summary-left">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="summary-icon">
                            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span className="summary-text">{runs.length} runs</span>
                    </div>
                    <div className="summary-divider-line"></div>
                    <div className="summary-right">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="summary-icon">
                            <path d="M21 16V8C20.9996 7.64927 20.9071 7.30481 20.7315 7.00116C20.556 6.69751 20.3037 6.44536 20 6.27L13 2.27C12.696 2.09446 12.3511 2.00205 12 2.00205C11.6489 2.00205 11.304 2.09446 11 2.27L4 6.27C3.69626 6.44536 3.44398 6.69751 3.26846 7.00116C3.09294 7.30481 3.00036 7.64927 3 8V16C3.00036 16.3507 3.09294 16.6952 3.26846 16.9988C3.44398 17.3025 3.69626 17.5546 4 17.73L11 21.73C11.304 21.9055 11.6489 21.9979 12 21.9979C12.3511 21.9979 12.696 21.9055 13 21.73L20 17.73C20.3037 17.5546 20.556 17.3025 20.7315 16.9988C20.9071 16.6952 20.9996 16.3507 21 16Z" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span className="summary-text">{totalPlannedQty.toLocaleString()} cans</span>
                    </div>
                </div>
            )}

            <div className="kanban-column-content">
                <SortableContext
                    items={runs.map(run => run.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {runs.length === 0 ? (
                        <div className="column-empty-state">
                            <div className="empty-icon">{getColumnIcon()}</div>
                            <p className="empty-text">No runs yet</p>
                            <p className="empty-hint">Drop cards here</p>
                        </div>
                    ) : (
                        children
                    )}
                </SortableContext>
            </div>
        </div>
    );
}
